import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ApiService } from '../api-service/api.service';
import { ProductService } from '../core/product.service';
import { BrandAssociation } from './brand';

export interface AddBrandAssociationResponse {
  associationKey: string;
  label: string;
  productKey: string;
  productUserId: string;
}

@Injectable({ providedIn: 'root' })
export class AssociationsApiService {
  constructor(private apiService: ApiService, private productService: ProductService) {}

  public deleteAccountGroupAssociation(userId: string, accountGroupId: string): Observable<void> {
    const associationId = `${userId}-${accountGroupId}-AGID`;
    const url = `/bc-admin/user/${userId}/permissions/delete/`;
    return this.apiService.post(url, 'association_key=' + associationId, {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
    });
  }

  public enableAllAccountAccess(accountGroupId: string, userId: string): Observable<void> {
    const url = '/_ajax/v1/manage-users/permission/enable-all/';
    const data = {
      accountGroupId: accountGroupId,
      userId: userId,
    };
    return this.apiService.post(url, data);
  }

  public disableAllAccountAccess(accountGroupId: string, userId: string): Observable<void> {
    const url = '/_ajax/v1/manage-users/permission/disable-all/';
    const data = {
      accountGroupId: accountGroupId,
      userId: userId,
    };
    return this.apiService.post(url, data);
  }

  public setAccountAccess(accountGroupId: string, userId: string, accountIds: string[]): Observable<void> {
    const url = '/_ajax/v1/manage-users/permission/set/';
    const data = {
      accountGroupId: accountGroupId,
      userId: userId,
      accountIds: accountIds,
    };
    return this.apiService.post(url, data);
  }

  public addAssociations(userId: string, accountGroupIds: string[]): Observable<void> {
    const url = '/_ajax/v1/associate-user-with-account-groups/';
    const data = {
      userId: userId,
      accountGroupIds: accountGroupIds,
    };
    return this.apiService.post(url, data);
  }

  public getAssociatedBrands(userId: string, partnerId: string): Observable<BrandAssociation[]> {
    const url = '/_ajax/v1/user-association-lookup/';
    const data = {
      userId: userId,
      partnerId: partnerId,
      productId: 'NB',
    };
    const urlWithParams = this.apiService.generateUrlWithParams(url, data);
    return this.apiService.get(urlWithParams).pipe(
      map((resp) => {
        let brands = [];
        for (let i = 0; i < resp.length; i++) {
          if (resp[i].productId === 'NB') {
            brands = resp[i].associations;
            break;
          }
        }
        return brands.map((b) => {
          return {
            name: b.label,
            associationId: b.associationId,
            path: b.accountId,
          };
        });
      }),
    );
  }

  public deleteBrandAssociation(userId: string, associationId: string): Observable<void> {
    const url = `/bc-admin/user/${userId}/permissions/delete/`;
    return this.apiService.post(url, 'association_key=' + encodeURIComponent(associationId), {
      headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
    });
  }

  public addBrandAssocation(userId: string, path: string): Observable<AddBrandAssociationResponse> {
    const encodedPath = encodeURIComponent(path);
    const url = `/bc-admin/user/${userId}/permissions/associate/`;
    return this.apiService
      .post(url, 'product_uid=' + encodedPath, {
        headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
      })
      .pipe(
        map((resp) => {
          return {
            associationKey: resp.association_key,
            label: resp.label,
            productKey: resp.product_key,
            productUserId: resp.productUserId,
          };
        }),
      );
  }
}
